import api from "../index";

// Check current user is super admin
export const currentSuperAdmin = async () => {
  return await api.post("/currentSuperAdmin", {});
};

// ********** Analytics ********** //
// Get super admin analytics
export const getSuperAdminAnalytics = async (timeRange?: string) => {
  const params = timeRange ? `?timeRange=${timeRange}` : "";
  return await api.get(`/super-admin/analytics${params}`);
};

// Get usage analytics
export const getUsageAnalytics = async (timeRange?: string) => {
  const params = timeRange ? `?timeRange=${timeRange}` : "";
  return await api.get(`/super-admin/analytics/usage${params}`);
};

// Get retention analytics
export const getRetentionAnalytics = async (timeRange?: string) => {
  const params = timeRange ? `?timeRange=${timeRange}` : "";
  return await api.get(`/super-admin/analytics/retention${params}`);
};

// ********** Admins ********** //
// Get all admins
export const getAllAdminsPaginated = async (
  page: any,
  perPage: any,
  searchText: any
) => {
  if (!searchText) {
    return await api.get(`/admins/paginated?page=${page}&perPage=${perPage}`);
  } else {
    return await api.get(
      `/admins/paginated?page=${page}&perPage=${perPage}&searchText=${searchText}`
    );
  }
};

// ********** Visitors ********** //
// Get all visitors
export const getAllVisitorsPaginated = async (
  page: any,
  perPage: any,
  searchText: any
) => {
  // If name or email is not provided, ignore it
  if (!searchText) {
    return await api.get(`/visitors/paginated?page=${page}&perPage=${perPage}`);
  } else {
    return await api.get(
      `/visitors/paginated?page=${page}&perPage=${perPage}&searchText=${searchText}`
    );
  }
};

// Get visitor analytics overview
export const getVisitorAnalyticsOverview = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/overview", {
    params: { dateRange },
  });
};

// Get URL performance analytics
export const getVisitorUrlAnalytics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/urls", {
    params: { dateRange },
  });
};

// Get geographic analytics
export const getVisitorGeographicAnalytics = async (
  dateRange: number = 365
) => {
  return await api.get("/visitors/analytics/geographic", {
    params: { dateRange },
  });
};

// Get traffic source analytics
export const getVisitorTrafficSourceAnalytics = async (
  dateRange: number = 365
) => {
  return await api.get("/visitors/analytics/sources", {
    params: { dateRange },
  });
};

// Get real-time activity
export const getVisitorRealTimeActivity = async (hours: number = 24) => {
  return await api.get("/visitors/analytics/realtime", {
    params: { hours },
  });
};

// Get temporal analytics
export const getVisitorTemporalAnalytics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/temporal", {
    params: { dateRange },
  });
};

// Get security analytics
export const getVisitorSecurityAnalytics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/security", {
    params: { dateRange },
  });
};

// Get advanced geographic analytics
export const getVisitorAdvancedGeographicAnalytics = async (
  dateRange: number = 365
) => {
  return await api.get("/visitors/analytics/geographic-advanced", {
    params: { dateRange },
  });
};

// Get campaign analytics
export const getVisitorCampaignAnalytics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/campaigns", {
    params: { dateRange },
  });
};

// Get device analytics
export const getVisitorDeviceAnalytics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/devices", {
    params: { dateRange },
  });
};

// Get engagement metrics
export const getVisitorEngagementMetrics = async (dateRange: number = 365) => {
  return await api.get("/visitors/analytics/engagement", {
    params: { dateRange },
  });
};

// Toggle disable user account
export const toggleDisableUserAccount = async (userId: any) => {
  return await api.put(`/user/disable/${userId}`);
};

// Get admin details by id
export const getAdminDetailsById = async (id: any) => {
  return await api.get(`/super/admin/${id}`);
};

// Get admin subscription history
export const getAdminSubscriptionHistory = async (id: any) => {
  return await api.get(`/super/admin/${id}/subscription/history`);
};

// Get admin payment history
export const getAdminPaymentHistory = async (id: any) => {
  return await api.get(`/super/admin/${id}/payment/history`);
};

// Add usage to admin
export const addUsageToAdmin = async (id: any, data: any) => {
  return await api.post(`/super/admin/${id}/usage/add`, data);
};

// ** USAGE ** //
// Get admin usage history
export const getAdminUsageHistory = async (id: any) => {
  return await api.get(`/super/admin/${id}/usage/history`);
};

// Get usage of admin by id
export const getAdminUsageById = async (usageId: any) => {
  return await api.get(`/super/admin/usage/${usageId}`);
};

// Update usage of admin
export const updateUsageOfAdmin = async (
  adminId: any,
  usageId: any,
  data: any
) => {
  return await api.put(`/super/admin/${adminId}/usage/${usageId}/update`, data);
};

// Delete usage records
export const deleteUsageRecords = async (adminId: any, data: any) => {
  return await api.put(`/super/admin/${adminId}/usage/delete`, data);
};

// Get all leads
export const getAllLeads = async () => {
  return await api.get("/leads");
};

// Delete user account
export const deleteUserAccount = async (data: any) => {
  return await api.post(`/user/delete`, data);
};

// In your API connectors file
export const searchUsers = (query: any) => {
  return api.get(`/super/users/search?query=${encodeURIComponent(query)}`);
};

// Get user count by date range
export const getUserCountByDateRange = async (dateRange: {
  startDate: Date;
  endDate: Date;
}) => {
  return await api.get("/super/users/date-range/count", {
    params: {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    },
  });
};

// ********** Refills Management ********** //
interface RefillStatisticsFilter {
  page?: number;
  limit?: number;
  plan?: string;
  status?: string;
  overdueOnly?: boolean;
  recentOnly?: boolean;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// Get refill statistics
export const getRefillStatistics = async (filter: RefillStatisticsFilter = {}) => {
  const params: Record<string, string> = {};

  if (filter.page) params.page = filter.page.toString();
  if (filter.limit) params.limit = filter.limit.toString();
  if (filter.plan) params.plan = filter.plan;
  if (filter.status) params.status = filter.status;
  if (filter.overdueOnly) params.overdueOnly = "true";
  if (filter.recentOnly) params.recentOnly = "true";
  if (filter.sortBy) params.sortBy = filter.sortBy;
  if (filter.sortOrder) params.sortOrder = filter.sortOrder;

  return await api.get("/super/refills/statistics", { params });
};

// Manual refill for single admin
export const manualRefillAdmin = async (adminId: string) => {
  return await api.post(`/super/refills/admin/${adminId}`);
};

// Bulk manual refill for admins
export const bulkManualRefillAdmins = async (adminIds: string[]) => {
  return await api.post("/super/refills/bulk", { adminIds });
};

// Trigger refill job manually
export const triggerRefillJob = async (
  jobType: "monthly" | "annual" | "free" | "lifetime"
) => {
  return await api.post("/super/refills/trigger-job", { jobType });
};

// Change admin plan
export const changeAdminPlan = async (adminId: string, planId: string) => {
  return await api.put(`/super/admin/${adminId}/plan`, { planId });
};